"use client";

import { useEffect, useState } from "react";
import {
  Checkbox,
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SMALL,
  CARD,
  HINT,
  LABEL,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import {
  ResultCard,
  ScriptTextArea,
} from "@/components/tools/transliteration-more/shared";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  type CharacterTable,
  type ChineseDirection,
  type ChineseRegion,
  convertCharacters,
  loadCharacterTable,
  loadVariantTable,
  markAmbiguous,
} from "@/lib/tool-kit/transliteration-more/chinese";

/** English strings. A translated content file overrides these through `ui`. */
export const CHINESE_SIMPLIFIED_TRADITIONAL_CONVERTER_STRINGS = {
  direction: "Direction",
  directions: {
    s2t: "Simplified → Traditional",
    t2s: "Traditional → Simplified",
  },
  inputLabels: {
    s2t: "Simplified Chinese text",
    t2s: "Traditional Chinese text",
  },
  inputHint:
    "Converted one character at a time. Words and phrases are not looked up, so regional vocabulary is not changed.",
  samples: {
    s2t: "他的头发很长，公司发展很快。我们在后面看书。",
    t2s: "他的頭髮很長，公司發展很快。乾淨的麵。",
  },
  sample: "Insert a sample",
  optionsLegend: "Options",
  regionLabel: "Traditional forms",
  regionOptions: {
    standard: "OpenCC standard (爲, 裏, 着)",
    tw: "Taiwan standard forms (為, 裡, 著)",
    hk: "Hong Kong forms (為, 裏, 着)",
  },
  mark: "Show each ambiguous character as 【first|other】 in the result",
  resultHeadings: { s2t: "Traditional Chinese", t2s: "Simplified Chinese" },
  resultUse:
    "Character-level conversion with OpenCC's default for each character. Check the flagged characters below.",
  loading: "Loading the character table…",
  loadError:
    "The character table could not be loaded. Check your connection and try again.",
  retry: "Try again",
  enterText: "Type or paste some Chinese text to convert.",
  copy: "Copy",
  copied: "Copied!",
  changed: "Characters changed",
  ambiguousCount: "Ambiguous characters",
  unchanged: "Chinese characters left as they were",
  ambiguousHeading: "Characters with more than one possible result",
  ambiguousIntro:
    "one:{count} character in your text has more than one correct conversion. The tool used the first choice; pick another if the word needs it.|other:{count} characters in your text have more than one correct conversion. The tool used the first choice for each; pick another where the word needs it.",
  ambiguousLabel: "{char} ({times}×)",
  validAsIs: "{char} is also correct as it stands.",
  noAmbiguous:
    "No character in this text has more than one possible conversion.",
  limitsNote:
    "Phrase-level conversion is not done. The same word can be written differently in mainland China, Taiwan and Hong Kong (软件, 軟體, 軟件), and a character-by-character tool cannot know which.",
  dataNote:
    "Character tables from OpenCC (Open Chinese Convert), used under the Apache License 2.0 and loaded when you first use each direction or regional option.",
  licenceLink: "Licence text",
  sourceLink: "OpenCC on GitHub",
} as const;

type Strings = typeof CHINESE_SIMPLIFIED_TRADITIONAL_CONVERTER_STRINGS;
type Status = "loading" | "ready" | "error";

function useCharacterTable(direction: ChineseDirection, region: ChineseRegion) {
  const [tables, setTables] = useState<{
    table: CharacterTable;
    variants: CharacterTable | undefined;
  } | null>(null);
  const [status, setStatus] = useState<Status>("loading");
  const [attempt, setAttempt] = useState(0);
  const regionKey = direction === "s2t" ? region : "standard";

  // biome-ignore lint/correctness/useExhaustiveDependencies: `attempt` re-runs the load when the reader retries.
  useEffect(() => {
    let cancelled = false;
    setStatus("loading");
    Promise.all([loadCharacterTable(direction), loadVariantTable(regionKey)])
      .then(([table, variants]) => {
        if (!cancelled) {
          setTables({ table, variants });
          setStatus("ready");
        }
      })
      .catch(() => {
        if (!cancelled) {
          setStatus("error");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [direction, regionKey, attempt]);

  return { tables, status, retry: () => setAttempt((n) => n + 1) };
}

function AmbiguousPanel({
  ambiguous,
  choices,
  setChoices,
  strings,
}: {
  ambiguous: ReturnType<typeof convertCharacters>["ambiguous"];
  choices: Record<string, string>;
  setChoices: (
    update: (c: Record<string, string>) => Record<string, string>
  ) => void;
  strings: Strings;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  return (
    <section className={`${CARD} space-y-3`}>
      <h3 className={WIDGET_H3}>{strings.ambiguousHeading}</h3>
      {ambiguous.length === 0 ? (
        <p className={HINT}>{strings.noAmbiguous}</p>
      ) : (
        <>
          <p className={HINT}>
            {t(tPlural(strings.ambiguousIntro, locale, ambiguous.length), {
              count: fmt(ambiguous.length),
            })}
          </p>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {ambiguous.map((item) => (
              <div className="space-y-1" key={item.char}>
                <SelectField
                  id={`zh-choice-${item.char.codePointAt(0)}`}
                  label={t(strings.ambiguousLabel, {
                    char: item.char,
                    times: fmt(item.count),
                  })}
                  onChange={(value) =>
                    setChoices((c) => ({ ...c, [item.char]: value }))
                  }
                  options={item.candidates.map((candidate) => ({
                    value: candidate,
                    label: candidate,
                  }))}
                  value={choices[item.char] ?? (item.candidates[0] as string)}
                />
                {item.validAsIs ? (
                  <p className={HINT}>
                    {t(strings.validAsIs, { char: item.char })}
                  </p>
                ) : null}
              </div>
            ))}
          </div>
        </>
      )}
    </section>
  );
}

export default function ChineseSimplifiedTraditionalConverterWidget() {
  const strings = useToolUi(CHINESE_SIMPLIFIED_TRADITIONAL_CONVERTER_STRINGS);
  const fmt = useToolNumber();
  const [direction, setDirection] = useState<ChineseDirection>("s2t");
  const [texts, setTexts] = useState<Record<ChineseDirection, string>>({
    s2t: strings.samples.s2t,
    t2s: strings.samples.t2s,
  });
  const [choices, setChoices] = useState<Record<string, string>>({});
  const [mark, setMark] = useState(false);
  const [region, setRegion] = useState<ChineseRegion>("standard");
  const { tables, status, retry } = useCharacterTable(direction, region);
  const text = texts[direction];
  const ready = status === "ready" && tables !== null;
  const variants = direction === "s2t" ? tables?.variants : undefined;
  const result = ready
    ? convertCharacters(text, tables.table, choices, variants)
    : null;
  const shown =
    ready && mark
      ? markAmbiguous(text, tables.table, choices, variants)
      : (result?.text ?? "");

  return (
    <div className="space-y-6">
      <PillGroup
        label={strings.direction}
        name="zh-direction"
        onChange={(next) => {
          setDirection(next);
          setChoices({});
        }}
        options={(["s2t", "t2s"] as const).map((value) => ({
          value,
          label: strings.directions[value],
        }))}
        value={direction}
      />
      <ScriptTextArea
        dir="ltr"
        hint={strings.inputHint}
        id="zh-input"
        label={strings.inputLabels[direction]}
        lang={direction === "s2t" ? "zh-Hans" : "zh-Hant"}
        onChange={(value) => {
          setTexts((all) => ({ ...all, [direction]: value }));
          setChoices({});
        }}
        value={text}
      />
      <div className="flex flex-wrap gap-2">
        <button
          className={BUTTON_SMALL}
          onClick={() => {
            setTexts((all) => ({
              ...all,
              [direction]: strings.samples[direction],
            }));
            setChoices({});
          }}
          type="button"
        >
          {strings.sample}
        </button>
      </div>
      <fieldset className="space-y-3">
        <legend className={LABEL}>{strings.optionsLegend}</legend>
        {direction === "s2t" ? (
          <div className="max-w-md">
            <SelectField<ChineseRegion>
              id="zh-region"
              label={strings.regionLabel}
              onChange={setRegion}
              options={[
                { value: "standard", label: strings.regionOptions.standard },
                { value: "tw", label: strings.regionOptions.tw },
                { value: "hk", label: strings.regionOptions.hk },
              ]}
              value={region}
            />
          </div>
        ) : null}
        <Checkbox checked={mark} label={strings.mark} onChange={setMark} />
      </fieldset>
      {status === "loading" ? <Notice>{strings.loading}</Notice> : null}
      {status === "error" ? (
        <div className="space-y-2">
          <ErrorNotice>{strings.loadError}</ErrorNotice>
          <button className={BUTTON_SMALL} onClick={retry} type="button">
            {strings.retry}
          </button>
        </div>
      ) : null}
      {text.trim() === "" ? <Notice>{strings.enterText}</Notice> : null}
      {result && text.trim() !== "" ? (
        <section aria-live="polite" className="space-y-3">
          <h2 className={WIDGET_H2}>{strings.resultHeadings[direction]}</h2>
          <StatGrid>
            <StatCard label={strings.changed} value={fmt(result.changed)} />
            <StatCard
              label={strings.ambiguousCount}
              value={fmt(result.ambiguous.length)}
            />
            <StatCard label={strings.unchanged} value={fmt(result.unchanged)} />
          </StatGrid>
          <ResultCard
            copiedLabel={strings.copied}
            copyLabel={strings.copy}
            lang={direction === "s2t" ? "zh-Hant" : "zh-Hans"}
            text={shown}
            title={strings.resultHeadings[direction]}
            use={strings.resultUse}
          />
          <AmbiguousPanel
            ambiguous={result.ambiguous}
            choices={choices}
            setChoices={setChoices}
            strings={strings}
          />
        </section>
      ) : null}
      <p className={HINT}>{strings.limitsNote}</p>
      <p className={HINT}>
        {strings.dataNote}{" "}
        <a
          className="underline"
          href="https://github.com/BYVoid/OpenCC"
          rel="noopener noreferrer"
          target="_blank"
        >
          {strings.sourceLink}
        </a>
        {" · "}
        <a className="underline" href="/data/opencc-LICENSE.txt">
          {strings.licenceLink}
        </a>
      </p>
    </div>
  );
}
